/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import { OnboardingModule } from '@coursera-web/cds-labs';
import snakeCase from 'lodash/snakeCase';

import { FormValidationStatus, Grid } from '@coursera/cds-core';

import ViewOnboardingModuleVisibilityTracker from 'bundles/onboarding-common/components/ViewOnboardingModuleVisibilityTracker';
import ModularOnboardingQuestionRenderer from 'bundles/onboarding-common/components/modular/ModularOnboardingQuestionRenderer';
import { useOnboardingOptions } from 'bundles/onboarding-common/contexts/OnboardingOptionsContext';
import { useOnboardingProgress } from 'bundles/onboarding-common/contexts/OnboardingProgressContext';
import { useOnboardingQuestionData } from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useFormEventingData from 'bundles/onboarding-common/hooks/useFormEventingData';
import useIsAdvancer from 'bundles/onboarding-common/hooks/useIsAdvancer';
import useSubmitAnswer from 'bundles/onboarding-common/hooks/useSubmitAnswer';
import useTrackOnboardingModuleButtonClick from 'bundles/onboarding-common/hooks/useTrackOnboardingModuleButtonClicks';
import { formatQuestionValueForEventing } from 'bundles/onboarding-common/utils/eventingUtils';
import { getQuestionTitleAndSupportingText } from 'bundles/onboarding-common/utils/modularOnboarding';

import _t from 'i18n!nls/onboarding-common';

const styles = {
  validationErrorMessage: css`
    background-color: transparent !important;

    span {
      color: var(--cds-color-neutral-primary);
    }
  `,
};

type Props = {
  onOnboardingCompletionSuccess: () => void;
  onDismiss: () => void;
};

const GenericOnboardingModuleRenderer = ({ onOnboardingCompletionSuccess, onDismiss }: Props) => {
  const {
    hasAnsweredQuestion,
    validationErrorMessage,
    resetQuestionData,
    questionValue,
    submitAnswerErrorMessage,
    setSubmitAnswerErrorMessage,
  } = useOnboardingQuestionData();
  const { questionIds, questionId, currentStep, setQuestionId, totalSteps } = useOnboardingProgress();
  const isAdvancer = useIsAdvancer();
  const { skillsType } = useOnboardingOptions();

  const { trackClickOnboardingModuleButton } = useTrackOnboardingModuleButtonClick();
  const { submitAnswer } = useSubmitAnswer();

  const [isDismissed, setIsDismissed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { formEventData } = useFormEventingData();

  if (!questionId || isDismissed) {
    return null;
  }

  const isLastStep = currentStep === totalSteps;
  const isFirstStep = currentStep === 1;
  const onboardingModuleType = 'generic' as const;
  const hasValidationError = !!validationErrorMessage;
  const errorMessage = validationErrorMessage || submitAnswerErrorMessage;

  const { title, supportingText } = getQuestionTitleAndSupportingText(questionId, {
    isAdvancer,
    isPopularSkills: skillsType === 'POPULAR_SKILLS',
  });

  const handleOnNextButtonClick = async () => {
    setIsSubmitting(true);
    try {
      await submitAnswer();
      setIsSubmitting(false);
    } catch {
      setSubmitAnswerErrorMessage(_t('There was an error saving your answer, please try again.'));
      setIsSubmitting(false);
      return;
    }

    trackClickOnboardingModuleButton({
      buttonName: 'continue_process',
      moduleType: onboardingModuleType,
      questionId,
      formEventData: {
        ...formEventData,
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        value: formatQuestionValueForEventing(questionValue),
      },
    });

    if (isLastStep) {
      trackClickOnboardingModuleButton({
        buttonName: 'onboarding_submit',
        moduleType: onboardingModuleType,
        questionId,
        formEventData: {
          ...formEventData,
          // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          value: formatQuestionValueForEventing(questionValue),
        },
      });
      onOnboardingCompletionSuccess();
    } else {
      resetQuestionData();
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      setQuestionId(questionIds[currentStep]);
    }
  };

  const handleOnDismissButtonClick = () => {
    trackClickOnboardingModuleButton({
      buttonName: 'onboarding_module_dismiss',
      moduleType: onboardingModuleType,
      questionId,
    });
    setIsDismissed(true);
    onDismiss();
  };

  const handleOnBackButtonClick = () => {
    trackClickOnboardingModuleButton({
      buttonName: 'onboarding_back_button',
      moduleType: onboardingModuleType,
      questionId,
      formEventData: {
        ...formEventData,
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        value: formatQuestionValueForEventing(questionValue),
      },
    });
    resetQuestionData();
    if (!isFirstStep) {
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      setQuestionId(questionIds[currentStep - 2]);
    }
  };

  const nextAction = {
    label: isLastStep ? _t('Apply') : _t('Next'),
    onClick: handleOnNextButtonClick,
    disabled: !hasAnsweredQuestion || hasValidationError || isSubmitting,
  };

  const dismissAction = {
    label: _t('Dismiss'),
    onClick: handleOnDismissButtonClick,
  };

  const backAction = !isFirstStep
    ? {
        label: _t('Back'),
        onClick: handleOnBackButtonClick,
      }
    : undefined;

  return (
    <ViewOnboardingModuleVisibilityTracker moduleType="generic" questionId={snakeCase(questionId)}>
      <OnboardingModule
        title={title}
        supportContent={supportingText}
        nextAction={nextAction}
        dismissAction={dismissAction}
        backAction={backAction}
      >
        <Grid container spacing={12}>
          <Grid item xs={12}>
            <ModularOnboardingQuestionRenderer />
          </Grid>
          {errorMessage && (
            <Grid item xs={12}>
              <FormValidationStatus validationStatus="error" label={errorMessage} css={styles.validationErrorMessage} />
            </Grid>
          )}
        </Grid>
      </OnboardingModule>
    </ViewOnboardingModuleVisibilityTracker>
  );
};

export default GenericOnboardingModuleRenderer;
